// Owner-dropdown data shared by the resume and JD tables: the app-wide
// owner list merged with consultant names (owners are usually consultants),
// case-insensitively unique, plus the add-if-missing action. The server
// dedupes case-insensitively and answers with the stored spelling, so
// "tanaka" resolves to an existing "Tanaka".

import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Consultant } from "@agp/shared";
import { useApi } from "@/hooks/useApi";

export function useOwnerOptions() {
  const api = useApi();
  const qc = useQueryClient();
  const ownersQuery = useQuery({
    queryKey: ["candidate-owners"],
    queryFn: () => api.get<{ owners: string[] }>("/api/candidate-owners"),
  });
  const consultantsQuery = useQuery({
    queryKey: ["consultants"],
    queryFn: () => api.get<{ consultants: Consultant[] }>("/api/consultants"),
  });

  const options = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const name of [
      ...(ownersQuery.data?.owners ?? []),
      ...(consultantsQuery.data?.consultants.map((c) => c.name) ?? []),
    ]) {
      const key = name.trim().toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(name.trim());
    }
    return out.sort((a, b) => a.localeCompare(b));
  }, [ownersQuery.data, consultantsQuery.data]);

  const addOwner = useCallback(
    async (name: string) => {
      const res = await api.post<{ name: string; owners: string[] }>("/api/candidate-owners", {
        name,
      });
      qc.setQueryData(["candidate-owners"], { owners: res.owners });
      return res.name;
    },
    [api, qc],
  );

  return { options, addOwner };
}
